import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { container } from "tsyringe";
import { RootState } from '../../app/store';
import { AnalyticsService } from "../../services/analytics";

export interface Timezone {
    name: string;
    offset: string;
    offsetHours: number;
    description: string;
}

export interface TimezoneConverterState {
    sourceTimezone: string;
    targetTimezone: string;
    sourceTime: string;
    convertedTime: string;
    resultCopied: boolean;
    timezones: Timezone[];
}

// Comprehensive list of timezones with GMT offsets
const timezones: Timezone[] = [
    { name: "GMT", offset: "GMT+0", offsetHours: 0, description: "Greenwich Mean Time" },
    { name: "UTC", offset: "UTC+0", offsetHours: 0, description: "Coordinated Universal Time" },
    { name: "EST", offset: "GMT-5", offsetHours: -5, description: "Eastern Standard Time" },
    { name: "CST", offset: "GMT-6", offsetHours: -6, description: "Central Standard Time" },
    { name: "MST", offset: "GMT-7", offsetHours: -7, description: "Mountain Standard Time" },
    { name: "PST", offset: "GMT-8", offsetHours: -8, description: "Pacific Standard Time" },
    { name: "AKST", offset: "GMT-9", offsetHours: -9, description: "Alaska Standard Time" },
    { name: "HST", offset: "GMT-10", offsetHours: -10, description: "Hawaii Standard Time" },
    { name: "AST", offset: "GMT-4", offsetHours: -4, description: "Atlantic Standard Time" },
    { name: "CET", offset: "GMT+1", offsetHours: 1, description: "Central European Time" },
    { name: "EET", offset: "GMT+2", offsetHours: 2, description: "Eastern European Time" },
    { name: "MSK", offset: "GMT+3", offsetHours: 3, description: "Moscow Time" },
    { name: "GST", offset: "GMT+4", offsetHours: 4, description: "Gulf Standard Time" },
    { name: "IST", offset: "GMT+5:30", offsetHours: 5.5, description: "Indian Standard Time" },
    { name: "CST", offset: "GMT+8", offsetHours: 8, description: "China Standard Time" },
    { name: "JST", offset: "GMT+9", offsetHours: 9, description: "Japan Standard Time" },
    { name: "AEST", offset: "GMT+10", offsetHours: 10, description: "Australian Eastern Standard Time" },
    { name: "NZST", offset: "GMT+12", offsetHours: 12, description: "New Zealand Standard Time" },
    { name: "WET", offset: "GMT+0", offsetHours: 0, description: "Western European Time" },
    { name: "BST", offset: "GMT+1", offsetHours: 1, description: "British Summer Time" },
    { name: "CEST", offset: "GMT+2", offsetHours: 2, description: "Central European Summer Time" },
    { name: "EEST", offset: "GMT+3", offsetHours: 3, description: "Eastern European Summer Time" },
    { name: "EDT", offset: "GMT-4", offsetHours: -4, description: "Eastern Daylight Time" },
    { name: "CDT", offset: "GMT-5", offsetHours: -5, description: "Central Daylight Time" },
    { name: "MDT", offset: "GMT-6", offsetHours: -6, description: "Mountain Daylight Time" },
    { name: "PDT", offset: "GMT-7", offsetHours: -7, description: "Pacific Daylight Time" },
    { name: "AKDT", offset: "GMT-8", offsetHours: -8, description: "Alaska Daylight Time" },
    { name: "HADT", offset: "GMT-9", offsetHours: -9, description: "Hawaii-Aleutian Daylight Time" },
    { name: "ADT", offset: "GMT-3", offsetHours: -3, description: "Atlantic Daylight Time" },
    { name: "BRT", offset: "GMT-3", offsetHours: -3, description: "Brasília Time" },
    { name: "ART", offset: "GMT-3", offsetHours: -3, description: "Argentina Time" },
    { name: "CLT", offset: "GMT-3", offsetHours: -3, description: "Chile Time" },
    { name: "UYT", offset: "GMT-3", offsetHours: -3, description: "Uruguay Time" },
    { name: "PYT", offset: "GMT-4", offsetHours: -4, description: "Paraguay Time" },
    { name: "BOT", offset: "GMT-4", offsetHours: -4, description: "Bolivia Time" },
    { name: "VET", offset: "GMT-4", offsetHours: -4, description: "Venezuela Time" },
    { name: "COT", offset: "GMT-5", offsetHours: -5, description: "Colombia Time" },
    { name: "PET", offset: "GMT-5", offsetHours: -5, description: "Peru Time" },
    { name: "ECT", offset: "GMT-5", offsetHours: -5, description: "Ecuador Time" },
    { name: "GALT", offset: "GMT-6", offsetHours: -6, description: "Galapagos Time" },
    { name: "CST", offset: "GMT-6", offsetHours: -6, description: "Central Standard Time (Mexico)" },
    { name: "MST", offset: "GMT-7", offsetHours: -7, description: "Mountain Standard Time (Mexico)" },
    { name: "PST", offset: "GMT-8", offsetHours: -8, description: "Pacific Standard Time (Mexico)" },
    { name: "WAT", offset: "GMT+1", offsetHours: 1, description: "West Africa Time" },
    { name: "CAT", offset: "GMT+2", offsetHours: 2, description: "Central Africa Time" },
    { name: "EAT", offset: "GMT+3", offsetHours: 3, description: "East Africa Time" },
    { name: "SAST", offset: "GMT+2", offsetHours: 2, description: "South Africa Standard Time" },
    { name: "WEST", offset: "GMT+1", offsetHours: 1, description: "Western European Summer Time" },
    { name: "CEST", offset: "GMT+2", offsetHours: 2, description: "Central European Summer Time" },
    { name: "EEST", offset: "GMT+3", offsetHours: 3, description: "Eastern European Summer Time" },
    { name: "MSD", offset: "GMT+4", offsetHours: 4, description: "Moscow Summer Time" },
    { name: "SAMT", offset: "GMT+4", offsetHours: 4, description: "Samara Time" },
    { name: "YEKT", offset: "GMT+5", offsetHours: 5, description: "Yekaterinburg Time" },
    { name: "OMST", offset: "GMT+6", offsetHours: 6, description: "Omsk Time" },
    { name: "NOVT", offset: "GMT+6", offsetHours: 6, description: "Novosibirsk Time" },
    { name: "KRAT", offset: "GMT+7", offsetHours: 7, description: "Krasnoyarsk Time" },
    { name: "IRKT", offset: "GMT+8", offsetHours: 8, description: "Irkutsk Time" },
    { name: "YAKT", offset: "GMT+9", offsetHours: 9, description: "Yakutsk Time" },
    { name: "VLAT", offset: "GMT+10", offsetHours: 10, description: "Vladivostok Time" },
    { name: "MAGT", offset: "GMT+11", offsetHours: 11, description: "Magadan Time" },
    { name: "PETT", offset: "GMT+12", offsetHours: 12, description: "Kamchatka Time" },
    { name: "AWST", offset: "GMT+8", offsetHours: 8, description: "Australian Western Standard Time" },
    { name: "ACST", offset: "GMT+9:30", offsetHours: 9.5, description: "Australian Central Standard Time" },
    { name: "AEST", offset: "GMT+10", offsetHours: 10, description: "Australian Eastern Standard Time" },
    { name: "ACWST", offset: "GMT+8:45", offsetHours: 8.75, description: "Australian Central Western Standard Time" },
    { name: "NZST", offset: "GMT+12", offsetHours: 12, description: "New Zealand Standard Time" },
    { name: "CHAST", offset: "GMT+12:45", offsetHours: 12.75, description: "Chatham Standard Time" },
    { name: "SST", offset: "GMT+8", offsetHours: 8, description: "Singapore Standard Time" },
    { name: "PHT", offset: "GMT+8", offsetHours: 8, description: "Philippine Time" },
    { name: "MYT", offset: "GMT+8", offsetHours: 8, description: "Malaysia Time" },
    { name: "WITA", offset: "GMT+8", offsetHours: 8, description: "Central Indonesian Time" },
    { name: "WIB", offset: "GMT+7", offsetHours: 7, description: "Western Indonesian Time" },
    { name: "WIT", offset: "GMT+9", offsetHours: 9, description: "Eastern Indonesian Time" },
    { name: "ICT", offset: "GMT+7", offsetHours: 7, description: "Indochina Time" },
    { name: "MMT", offset: "GMT+6:30", offsetHours: 6.5, description: "Myanmar Time" },
    { name: "NPT", offset: "GMT+5:45", offsetHours: 5.75, description: "Nepal Time" },
    { name: "BTT", offset: "GMT+6", offsetHours: 6, description: "Bhutan Time" },
    { name: "BDT", offset: "GMT+6", offsetHours: 6, description: "Bangladesh Time" },
    { name: "PKT", offset: "GMT+5", offsetHours: 5, description: "Pakistan Time" },
    { name: "AFT", offset: "GMT+4:30", offsetHours: 4.5, description: "Afghanistan Time" },
    { name: "TJT", offset: "GMT+5", offsetHours: 5, description: "Tajikistan Time" },
    { name: "TMT", offset: "GMT+5", offsetHours: 5, description: "Turkmenistan Time" },
    { name: "UZT", offset: "GMT+5", offsetHours: 5, description: "Uzbekistan Time" },
    { name: "KGT", offset: "GMT+6", offsetHours: 6, description: "Kyrgyzstan Time" },
    { name: "ALMT", offset: "GMT+6", offsetHours: 6, description: "Almaty Time" },
    { name: "QYZT", offset: "GMT+6", offsetHours: 6, description: "Qyzylorda Time" },
    { name: "AQTT", offset: "GMT+5", offsetHours: 5, description: "Aqtobe Time" },
    { name: "ORAT", offset: "GMT+5", offsetHours: 5, description: "Oral Time" },
    { name: "AZT", offset: "GMT+4", offsetHours: 4, description: "Azerbaijan Time" },
    { name: "AMT", offset: "GMT+4", offsetHours: 4, description: "Armenia Time" },
    { name: "GET", offset: "GMT+4", offsetHours: 4, description: "Georgia Time" },
    { name: "TRT", offset: "GMT+3", offsetHours: 3, description: "Turkey Time" },
    { name: "EET", offset: "GMT+2", offsetHours: 2, description: "Eastern European Time" },
    { name: "CET", offset: "GMT+1", offsetHours: 1, description: "Central European Time" },
    { name: "WET", offset: "GMT+0", offsetHours: 0, description: "Western European Time" },
    { name: "GMT", offset: "GMT+0", offsetHours: 0, description: "Greenwich Mean Time" },
    { name: "UTC", offset: "UTC+0", offsetHours: 0, description: "Coordinated Universal Time" }
];

// Function to detect user's timezone and find the closest match
const getUserTimezone = (): string => {
    try {
        // Try to get the browser's timezone name first
        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        
        // Map common browser timezone names to our timezone abbreviations
        const timezoneMap: { [key: string]: string } = {
            // Africa
            'Africa/Johannesburg': 'SAST',
            'Africa/Cairo': 'EET',
            'Africa/Lagos': 'WAT',
            'Africa/Nairobi': 'EAT',
            'Africa/Casablanca': 'WET',
            'Africa/Algiers': 'CET',
            
            // Europe
            'Europe/London': 'GMT',
            'Europe/Paris': 'CET',
            'Europe/Berlin': 'CET',
            'Europe/Rome': 'CET',
            'Europe/Madrid': 'CET',
            'Europe/Amsterdam': 'CET',
            'Europe/Brussels': 'CET',
            'Europe/Vienna': 'CET',
            'Europe/Zurich': 'CET',
            'Europe/Stockholm': 'CET',
            'Europe/Oslo': 'CET',
            'Europe/Copenhagen': 'CET',
            'Europe/Helsinki': 'EET',
            'Europe/Riga': 'EET',
            'Europe/Tallinn': 'EET',
            'Europe/Vilnius': 'EET',
            'Europe/Athens': 'EET',
            'Europe/Bucharest': 'EET',
            'Europe/Sofia': 'EET',
            'Europe/Belgrade': 'CET',
            'Europe/Budapest': 'CET',
            'Europe/Prague': 'CET',
            'Europe/Warsaw': 'CET',
            'Europe/Kiev': 'EET',
            'Europe/Moscow': 'MSK',
            'Europe/Istanbul': 'TRT',
            
            // Americas
            'America/New_York': 'EST',
            'America/Chicago': 'CST',
            'America/Denver': 'MST',
            'America/Los_Angeles': 'PST',
            'America/Anchorage': 'AKST',
            'America/Honolulu': 'HST',
            'America/Toronto': 'EST',
            'America/Vancouver': 'PST',
            'America/Montreal': 'EST',
            'America/Edmonton': 'MST',
            'America/Winnipeg': 'CST',
            'America/Regina': 'CST',
            'America/Halifax': 'AST',
            'America/St_Johns': 'NST',
            'America/Sao_Paulo': 'BRT',
            'America/Argentina/Buenos_Aires': 'ART',
            'America/Santiago': 'CLT',
            'America/Montevideo': 'UYT',
            'America/Asuncion': 'PYT',
            'America/La_Paz': 'BOT',
            'America/Caracas': 'VET',
            'America/Bogota': 'COT',
            'America/Lima': 'PET',
            'America/Guayaquil': 'ECT',
            'America/Mexico_City': 'CST',
            'America/Tijuana': 'PST',
            'America/Phoenix': 'MST',
            
            // Asia
            'Asia/Tokyo': 'JST',
            'Asia/Shanghai': 'CST',
            'Asia/Seoul': 'KST',
            'Asia/Singapore': 'SST',
            'Asia/Manila': 'PHT',
            'Asia/Kuala_Lumpur': 'MYT',
            'Asia/Jakarta': 'WIB',
            'Asia/Makassar': 'WITA',
            'Asia/Jayapura': 'WIT',
            'Asia/Bangkok': 'ICT',
            'Asia/Ho_Chi_Minh': 'ICT',
            'Asia/Yangon': 'MMT',
            'Asia/Kathmandu': 'NPT',
            'Asia/Thimphu': 'BTT',
            'Asia/Dhaka': 'BDT',
            'Asia/Karachi': 'PKT',
            'Asia/Kabul': 'AFT',
            'Asia/Dushanbe': 'TJT',
            'Asia/Ashgabat': 'TMT',
            'Asia/Tashkent': 'UZT',
            'Asia/Bishkek': 'KGT',
            'Asia/Almaty': 'ALMT',
            'Asia/Qyzylorda': 'QYZT',
            'Asia/Aqtobe': 'AQTT',
            'Asia/Oral': 'ORAT',
            'Asia/Baku': 'AZT',
            'Asia/Yerevan': 'AMT',
            'Asia/Tbilisi': 'GET',
            'Asia/Dubai': 'GST',
            'Asia/Muscat': 'GST',
            'Asia/Kuwait': 'AST',
            'Asia/Riyadh': 'AST',
            'Asia/Qatar': 'AST',
            'Asia/Bahrain': 'AST',
            'Asia/Tehran': 'IRST',
            'Asia/Kolkata': 'IST',
            'Asia/Colombo': 'IST',
            'Asia/Rangoon': 'MMT',
            
            // Australia/Oceania
            'Australia/Sydney': 'AEST',
            'Australia/Melbourne': 'AEST',
            'Australia/Brisbane': 'AEST',
            'Australia/Adelaide': 'ACST',
            'Australia/Perth': 'AWST',
            'Australia/Darwin': 'ACST',
            'Australia/Hobart': 'AEST',
            'Pacific/Auckland': 'NZST',
            'Pacific/Chatham': 'CHAST',
            'Pacific/Fiji': 'FJT',
            'Pacific/Guam': 'ChST',
            'Pacific/Honolulu': 'HST',
            
            // UTC/GMT
            'UTC': 'UTC',
            'GMT': 'GMT'
        };
        
        // Check if we have a direct mapping for the browser timezone
        if (timezoneMap[browserTimezone]) {
            return timezoneMap[browserTimezone];
        }
        
        // Fallback to offset-based detection
        const userOffset = -new Date().getTimezoneOffset() / 60;
        const closestTimezone = timezones.find(tz => tz.offsetHours === userOffset);
        
        if (closestTimezone) {
            return closestTimezone.name;
        }
        
        return "UTC";
    } catch (error) {
        // Fallback to offset-based detection if browser timezone detection fails
        try {
            const userOffset = -new Date().getTimezoneOffset() / 60;
            const closestTimezone = timezones.find(tz => tz.offsetHours === userOffset);
            return closestTimezone ? closestTimezone.name : "UTC";
        } catch (fallbackError) {
            return "UTC";
        }
    }
};

const initialState: TimezoneConverterState = {
    sourceTimezone: getUserTimezone(),
    targetTimezone: "EST",
    sourceTime: (() => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    })(), // Current local time in YYYY-MM-DDTHH:MM format
    convertedTime: "",
    resultCopied: false,
    timezones: timezones
};

export const TimezoneConverterSlice = createSlice({
    name: 'timezoneConverter',
    initialState,
    reducers: {
        sourceTimezoneChanged: (state, action: PayloadAction<string>) => {
            state.sourceTimezone = action.payload;
        },
        targetTimezoneChanged: (state, action: PayloadAction<string>) => {
            state.targetTimezone = action.payload;
        },
        sourceTimeChanged: (state, action: PayloadAction<string>) => {
            state.sourceTime = action.payload;
        },
        convertTime: (state) => {
            const analyticsService = container.resolve(AnalyticsService);
            analyticsService.LogEvent({    
                category: 'Button Click',
                action: 'Convert Clicked',
                label: 'Timezone Converter',
            });

            try {
                // Parse the source time
                const sourceDate = new Date(state.sourceTime);
                if (isNaN(sourceDate.getTime())) {
                    state.convertedTime = "Invalid date";
                    return;
                }

                // Get timezone offsets
                const sourceTz = state.timezones.find(tz => tz.name === state.sourceTimezone);
                const targetTz = state.timezones.find(tz => tz.name === state.targetTimezone);

                if (!sourceTz || !targetTz) {
                    state.convertedTime = "Invalid timezone";
                    return;
                }

                // Calculate the difference in hours
                const offsetDifference = targetTz.offsetHours - sourceTz.offsetHours;
                
                // Apply the offset
                const convertedDate = new Date(sourceDate.getTime() + (offsetDifference * 60 * 60 * 1000));
                
                // Format the result to show local time in target timezone
                const year = convertedDate.getFullYear();
                const month = String(convertedDate.getMonth() + 1).padStart(2, '0');
                const day = String(convertedDate.getDate()).padStart(2, '0');
                const hours = String(convertedDate.getHours()).padStart(2, '0');
                const minutes = String(convertedDate.getMinutes()).padStart(2, '0');
                
                state.convertedTime = `${year}-${month}-${day} ${hours}:${minutes}`;
            } catch (error) {
                state.convertedTime = "Error converting time";
            }
        },
        resultCopyTapped: (state) => {
            state.resultCopied = true;
        },
        resetToNow: (state) => {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            state.sourceTime = `${year}-${month}-${day}T${hours}:${minutes}`;
        },
    }
});

export const { sourceTimezoneChanged, targetTimezoneChanged, sourceTimeChanged, convertTime, resultCopyTapped, resetToNow } = TimezoneConverterSlice.actions;

export const selectState = (state: RootState) => state.timezoneConverter;

export default TimezoneConverterSlice.reducer;
